import {
  BEGINNING_START_DAY,
  END_START_DAY,
  MIDDLE_START_DAY,
  dayOfYearLocal,
  dibelsGradeNumber,
  getDibelsGradePeriod,
  gradePeriodOf,
  type DibelsGrade,
  getDibelsPeriod,
  getDibelsPeriodForClientDate,
  getLocalIsoDate,
  type DibelsPeriod,
} from "./dibels-calendar";
import type { WrfPercentileStatus } from "../contracts/orientation";

export {
  dibelsGradeNumber,
  getDibelsGradePeriod,
  gradePeriodOf,
  type DibelsGrade,
  getDibelsPeriod,
  getDibelsPeriodForClientDate,
  getLocalIsoDate,
  type DibelsPeriod,
};

// Bindery-era names for ./dibels-calendar's boundaries, kept because importers
// and packages/fluency-model still cite them. Never restate a boundary here.
export const SPRING_START_DAY = END_START_DAY;
export const SUMMER_START_DAY = BEGINNING_START_DAY;
export const FALL_WINTER_START_DAY = MIDDLE_START_DAY;

export interface LnfLetter {
  letter: string;
  sequence: number;
}

export interface LnfForm {
  formId: string;
  letters: LnfLetter[];
}

// Pinned, not "latest available": loading a newer norms year must never
// switch the form mid-study.
export const LNF_FORM_YEAR = 2020;

export type LnfFormGrade = "K" | "1";

export function getLnfFormGrade(trueGrade: number | null): LnfFormGrade {
  if (trueGrade === null || trueGrade <= 0) return "K";
  return "1";
}

export function buildLnfFormId(
  grade: LnfFormGrade,
  period: DibelsPeriod,
  year: number,
): string {
  return `${grade}.${period}.${year}`;
}

/** A DIBELS level as `grade.period`, "1.2" for first grade middle of year:
 *  the level a score's norms are looked up against. */
export interface DibelsLevel {
  grade: number;
  gradePeriod: string;
}

const DIBELS_GRADE_PERIOD = /^(K|0|[1-9]|1[0-2])\.([123])$/;

/** The grade (K = 0) of a `grade.period` level, or null when it is not one. */
export function gradeOfGradePeriod(gradePeriod: string): number | null {
  const match = DIBELS_GRADE_PERIOD.exec(gradePeriod);
  if (match === null) return null;
  const grade = match[1];
  return grade === "K" ? 0 : Number(grade);
}

const DIBELS_PERIODS: readonly DibelsPeriod[] = ["Beginning", "Middle", "End"];

/** The level an LNF form id (`K.Beginning.2020`) was built for. */
export function lnfFormLevelOf(formId: string): DibelsLevel | null {
  const [grade, period] = formId.split(".");
  const found = DIBELS_PERIODS.find((p) => p === period);
  if (found === undefined || (grade !== "K" && grade !== "1")) return null;
  return {
    grade: dibelsGradeNumber(grade),
    gradePeriod: gradePeriodOf(grade, found),
  };
}

export function lnfFormIdMatchesGrade(
  formId: string | undefined,
  grade: LnfFormGrade,
): boolean {
  return formId !== undefined && formId.startsWith(`${grade}.`);
}

export interface WrfWord {
  word: string;
  sequence: number;
}

export interface WrfPercentileResult {
  percentile: number;
  found: boolean;
}

export type { WrfPercentileStatus };

export interface WrfNormsPercentile {
  status: WrfPercentileStatus;
  percentile: number | null;
  bound?: number;
}

export interface WrfNormsRow {
  rawScore: number;
  percentile: number | null;
}

// A missing row or a NULL percentile (the 2022 norms hold NULL at high raw
// scores) must never read as percentile 0.
export function classifyWrfPercentile(
  rawScore: number,
  rows: readonly WrfNormsRow[],
): WrfNormsPercentile {
  const normed = rows.filter(
    (r): r is { rawScore: number; percentile: number } =>
      r.percentile !== null && Number.isFinite(r.percentile),
  );
  const exact = normed.find((r) => r.rawScore === rawScore);
  if (exact) return { status: "norms", percentile: exact.percentile };
  if (normed.length === 0 || !Number.isFinite(rawScore)) {
    return { status: "no_norms", percentile: null };
  }
  let lowest = normed[0]!;
  let highest = normed[0]!;
  for (const r of normed) {
    if (r.rawScore < lowest.rawScore) lowest = r;
    if (r.rawScore > highest.rawScore) highest = r;
  }
  if (rawScore > highest.rawScore) {
    return {
      status: "above_norms",
      percentile: null,
      bound: highest.percentile,
    };
  }
  if (rawScore < lowest.rawScore) {
    return {
      status: "below_norms",
      percentile: null,
      bound: lowest.percentile,
    };
  }
  return { status: "no_norms", percentile: null };
}

// Only a percentile certainly below the threshold downgrades; unknown never does.
export function isBelowWrfPercentile(
  lookup: WrfNormsPercentile,
  threshold: number,
): boolean {
  switch (lookup.status) {
    case "norms":
      return lookup.percentile !== null && lookup.percentile < threshold;
    case "below_norms":
      return lookup.bound !== undefined && lookup.bound <= threshold;
    default:
      return false;
  }
}

export function formatWrfPercentile(lookup: WrfNormsPercentile): string {
  if (lookup.status === "norms" && lookup.percentile !== null) {
    return String(lookup.percentile);
  }
  if (lookup.status === "below_norms" && lookup.bound !== undefined) {
    return `<${lookup.bound}`;
  }
  if (lookup.status === "above_norms" && lookup.bound !== undefined) {
    return `≥${lookup.bound}`;
  }
  return "unknown";
}

// Stays a number for existing readers of `phases.awrf.percentile`; the stored
// `percentileStatus` says what it means.
export function storedWrfPercentile(lookup: WrfNormsPercentile): number {
  if (lookup.status === "norms" && lookup.percentile !== null) {
    return lookup.percentile;
  }
  if (lookup.status === "above_norms" && lookup.bound !== undefined) {
    return lookup.bound;
  }
  return 0;
}

export interface InstructionalGradeResult {
  gradePeriod: string;
  percentile: number;
  found: boolean;
}

export interface OrfPassage {
  gradePeriod: string;
  passageTitle: string;
  passageText: string;
  words: string[];
  totalWords: number;
  skipped?: boolean; // fetch failed after retries
}

export const GRADE_WORD_LIMITS: Record<number, number> = {
  0: 36,
  1: 81,
  2: 90,
  3: 99,
};

export function calculateGradePeriod(
  grade: number,
  date: Date = new Date(),
): string {
  return getDibelsGradePeriod(grade, date);
}

export function calculateDayOfYear(date: Date): number {
  return dayOfYearLocal(date);
}

export function getGradeWordLimit(grade: number): number {
  return GRADE_WORD_LIMITS[grade] ?? 36;
}

export function gradeToString(grade: number): string {
  return grade === 0 ? "K" : String(grade);
}
